import { computed, makeObservable } from 'mobx';
import { BonusCreationStatusType, CriteriaType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { ApiGetBonusCreationsType } from 'src/domains/casino/models/models';
import { apiDateToDateTime } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { Amount } from 'src_common/common/amount/Amount';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { DateTime } from 'src_common/utils/time/time';

export class BonusCreationModel {
    public readonly statusForm: FormInputState<BonusCreationStatusType, string>;

    public constructor(public data: ApiGetBonusCreationsType) {
        makeObservable(this);
        this.statusForm = FormInputState.new(this.data.status);
    }

    @computed public get id(): string {
        return this.data.bonusCreationId;
    }

    @computed public get name(): string {
        return this.data.name ?? '';
    }

    @computed public get terms(): string {
        return this.data.terms ?? '';
    }

    @computed public get rewardName(): string {
        return this.data.rewardName ?? '';
    }

    @computed public get rewardType(): 'free-bet' | 'free-spin' | null {
        const rewardType = this.data.rewardType ?? null;

        if (rewardType === 'free-spin') {
            return 'free-spin';
        } else if (rewardType === 'free-bet') {
            return 'free-bet';
        }

        return null;
    }

    @computed public get rewardTypeForView(): string {
        return this.data.rewardType?.replace('-', ' ') ?? '';
    }

    @computed public get usageConditions(): CriteriaType[] | null | undefined {
        return this.data.usageConditions;
    }

    @computed public get totalBet(): number | null | undefined {
        return this.data.totalBet;
    }

    @computed public get playthrough(): string | null {
        return this.data.playthrough !== null && this.data.playthrough !== undefined
            ? new Amount(this.data.playthrough.toString()).format('GBP', { withoutCurrencySymbol: true })
            : null;
    }

    @computed public get playthroughEligibleGames(): string[] | null | undefined {
        return this.data.playthroughEligibleGames;
    }

    @computed public get rewardAmount(): Amount | null {
        return this.data.rewardAmount === null || this.data.rewardAmount === undefined
            ? null
            : new Amount(this.data.rewardAmount.toString());
    }

    @computed public get rewardExpiryDate(): DateTime | null {
        return apiDateToDateTime(this.data.rewardExpiryAt);
    }

    @computed public get isExpired(): boolean {
        const now = DateTime.current();
        return this.rewardExpiryDate?.isBefore(now) ?? false;
    }

    @computed public get rewardExpiryDisplay(): string {
        if (this.rewardExpiryDate === null && this.relativeExpiryDays === null) return '';

        if (this.rewardExpiryDate === null) {
            return `${this.relativeExpiryDays} days after reward`;
        } else {
            return this.rewardExpiryDate.format('DD/MM/YYYY | HH:mm');
        }
    }

    @computed public get relativeExpiryDays(): number | null {
        return this.data.relativeExpiryDays ?? null;
    }

    @computed public get expiryTypeForView(): { expiryType: string; labelInfo: string } | null | undefined {
        const expiryAt = this.rewardExpiryDateApi;
        return expiryAt === null
            ? { expiryType: 'Relative expiry', labelInfo: 'Number of days' }
            : { expiryType: 'Absolute expiry', labelInfo: 'End Date & Time (UK Time)' };
    }

    @computed public get rewardExpiryDateApi(): string | null | undefined {
        return this.data.rewardExpiryAt;
    }

    @computed public get installmentCount(): number | null | undefined {
        return this.data.installmentCount;
    }

    @computed public get rewardInterval(): number | null | undefined {
        return this.data.rewardInterval;
    }

    @computed public get scheduledReward(): boolean | null | undefined {
        return this.data.scheduledReward;
    }

    @computed public get rewardDate(): string | null | undefined {
        return this.data.rewardDate;
    }

    @computed public get status(): ApiGetBonusCreationsType['status'] {
        return this.statusForm.value;
    }
}
